import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Biotech as BiotechIcon,
  Image as ImagingIcon,
  Warning as WarningIcon,
  Block as RejectIcon,
  MoreHoriz as MoreIcon,
  CheckCircle as AssignedIcon,
  Cancel as RejectedIcon,
  Inbox as PendingIcon,
  Replay as ReopenIcon,
} from '@mui/icons-material';
import { useClinicTimezone, useFormatInstant } from '../../hooks/useClinicTimezone';
import { formatAnchoredDayOrInstant } from '../../utils/dateUtils';
import { UnallocatedItem, MatchSuggestion } from '../../types/inbox';
import ModalityBadge from '../Keystone/ModalityBadge';

interface Props {
  item: UnallocatedItem;
  suggestions: MatchSuggestion[];
  onAssignTopMatch?: (horseId: string) => void;
  onAssignManual?: () => void;
  onReject?: (status: 'REJECTED' | 'NOT_OURS', reason?: string) => void;
  onReopen?: () => void;
  busy?: boolean;
}

const STATUS_CHIP: Record<UnallocatedItem['triageStatus'], { label: string; color: 'warning' | 'success' | 'default' | 'error'; Icon: any }> = {
  PENDING: { label: 'Pending review', color: 'warning', Icon: PendingIcon },
  ASSIGNED: { label: 'Assigned', color: 'success', Icon: AssignedIcon },
  REJECTED: { label: 'Rejected', color: 'default', Icon: RejectedIcon },
  NOT_OURS: { label: 'Not our clinic', color: 'default', Icon: RejectIcon },
};

const UnallocatedItemCard: React.FC<Props> = ({
  item,
  suggestions,
  onAssignTopMatch,
  onAssignManual,
  onReject,
  onReopen,
  busy,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const status = STATUS_CHIP[item.triageStatus];
  const StatusIcon = status.Icon;
  /*
   * INSTANTS on this card render in the CLINIC's zone, not the viewer's — the
   * triage inbox is a clinic-wide queue that relief and remote staff work from,
   * and "Arrived 11:40 PM" has to mean the clinic's 11:40 PM (DATE-KINDS §2.3).
   */
  const formatInstant = useFormatInstant();
  // The same zone, unbound: `reportDate`'s lab arm needs the SHAPE rule, whose
  // instant half takes a zone as a plain argument (see the render).
  const clinicTz = useClinicTimezone();

  const top = suggestions[0];
  const highConfidence = top && top.score >= 80;
  const isPending = item.triageStatus === 'PENDING';
  const isImaging = item.kind === 'imaging_study';

  return (
    <Paper variant="outlined" sx={{ p: 2, opacity: isPending ? 1 : 0.7 }}>
      {/* Header row */}
      <Box display="flex" alignItems="flex-start" gap={1.5}>
        {/* Kind icon / modality badge */}
        {isImaging ? (
          <ModalityBadge name={item.modality} size={36} />
        ) : (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: 'primary.50',
              color: 'primary.main',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <BiotechIcon fontSize="small" />
          </Box>
        )}

        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2">
              {isImaging
                ? `${item.modality ?? 'Imaging'}${item.bodyRegion ? ` — ${item.bodyRegion}` : ''}`
                : item.panelName ?? 'Lab result'}
            </Typography>
            <Chip
              label={item.source}
              size="small"
              variant="outlined"
              icon={isImaging ? <ImagingIcon fontSize="small" /> : <BiotechIcon fontSize="small" />}
            />
            {!!item.abnormalCount && item.abnormalCount > 0 && (
              <Chip
                icon={<WarningIcon fontSize="small" />}
                label={`${item.abnormalCount} abnormal`}
                size="small"
                color="error"
              />
            )}
            <Chip icon={<StatusIcon fontSize="small" />} label={status.label} size="small" color={status.color} variant={isPending ? 'filled' : 'outlined'} />
          </Box>
          <Typography variant="caption" color="text.secondary" component="div" mt={0.25}>
            {/* INSTANT — `LabResult.arrivedAt` / `keystone_studies."arrivedAt"`
              * are both `@default(now())` ingestion stamps (DATE-KINDS §1.3). */}
            Arrived {formatInstant(item.arrivedAt, 'MMM d, h:mm a')}
            {item.accessionNumber ? ` · Acc #${item.accessionNumber}` : ''}
            {/* MIXED PER ROW — the documented per-row case (DATE-KINDS §2.4).
              * `reportDate` is two different columns depending on `item.kind`
              * (unallocated-results.service.ts toItemFromLab / toItemFromStudy).
              *
              * imaging_study -> keystone `study_date`, written from DICOM
              *   `StudyDateTime` (keystone-poller.service.ts:473) — a real
              *   moment, so it renders in the CLINIC's zone.
              *
              * lab_result -> `LabResult.reportDate`, and every lab row that can
              *   reach this inbox was written by the IDEXX mapper: `triageStatus`
              *   defaults to ALLOCATED (schema.prisma:1456) and the hand-entered
              *   writers (lab-results.service.ts:164, sync.service.ts:1476) never
              *   set it, the inbox's filter never returns ALLOCATED
              *   (unallocated-results.service.ts:343-356), and the ONLY writer of
              *   PENDING is the mapper (idexx-result-mapper.service.ts:81), whose
              *   `reportDate` is `parseIsoOrNow(orderReceivedDate ?? updatedDate)`.
              *
              *   That USED TO make the arm UNCERTAIN and it was left on the
              *   re-wrapping browser-zone idiom the ratchet counts: IDEXX's wire
              *   shape is not observable from this repo, and date-parting a real
              *   stamp prints the NEXT day for an evening result. The SHAPE rule settles it without needing the
              *   sample, because it is right in both readings — a stamp carrying
              *   a time takes the instant arm (a moment, in the clinic's zone),
              *   and a date-only string parses to exact UTC midnight, which IS a
              *   day and is read from its date part. Same helper, same spelling
              *   set and same reasoning as `Payment.paymentDate`
              *   (`dateUtils.formatAnchoredDayOrInstant`), and the chart's own
              *   render of this column now uses it too, so the two screens agree
              *   about one row for the first time. */}
            {item.reportDate
              ? ` · Report ${
                  isImaging
                    ? formatInstant(item.reportDate, 'MMM d, yyyy')
                    : formatAnchoredDayOrInstant(item.reportDate, clinicTz, 'MMM d, yyyy')
                }`
              : ''}
          </Typography>
        </Box>

        {isPending && (onReject || onAssignManual) && (
          <>
            <IconButton size="small" onClick={e => setMenuAnchor(e.currentTarget)} disabled={busy}>
              <MoreIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              {onAssignManual && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onAssignManual();
                  }}
                >
                  <ListItemIcon><AssignedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Assign to patient..." />
                </MenuItem>
              )}
              {onReject && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onReject('REJECTED', 'Duplicate / not actionable');
                  }}
                >
                  <ListItemIcon><RejectedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Reject (duplicate / not actionable)" />
                </MenuItem>
              )}
              {onReject && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onReject('NOT_OURS', 'Not our clinic');
                  }}
                >
                  <ListItemIcon><RejectIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Not our clinic" />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Box>

      {/* What the report said */}
      <Box mt={1} pl={6.5}>
        <Typography variant="body2" color="text.secondary">
          <strong>Patient on report:</strong> {item.patientNameOnReport ?? '—'}
          {item.patientIdOnReport ? ` (${item.patientIdOnReport})` : ''}
          {item.ownerOnReport ? ` · Owner: ${item.ownerOnReport}` : ''}
        </Typography>
        {item.rawNotes && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic', mt: 0.25 }}>
            {item.rawNotes}
          </Typography>
        )}
      </Box>

      {/* Triage outcome (when not PENDING) */}
      {!isPending && (
        <Box mt={1.5} pl={6.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            {/* INSTANT — stamped `new Date()` at triage time
              * (unallocated-results.service.ts:128/212/252). */}
            {item.triagedAt ? formatInstant(item.triagedAt, 'MMM d, h:mm a') : ''}
            {item.triagedBy ? ` · by ${item.triagedBy}` : ''}
            {item.rejectReason ? ` · ${item.rejectReason}` : ''}
          </Typography>
          {onReopen && (
            <Tooltip title="Move back to Pending">
              <Button size="small" startIcon={<ReopenIcon fontSize="small" />} onClick={onReopen} disabled={busy}>
                Re-open
              </Button>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Suggested matches + actions (only when PENDING) */}
      {isPending && (
        <Box mt={1.5} pl={6.5}>
          {suggestions.length === 0 ? (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
              No matching patient found in this clinic. Use "Assign to patient..." to search manually, or reject.
            </Typography>
          ) : (
            <Stack spacing={0.5} mb={1.5}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Suggested matches
              </Typography>
              {suggestions.slice(0, 3).map(s => (
                <Box key={s.horseId} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <Typography variant="body2">
                    {s.horseName}
                    {s.patientId ? (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' · '}
                        {s.patientId}
                      </Typography>
                    ) : null}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' — '}
                      {s.reasons.join(', ')}
                    </Typography>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: s.score >= 80 ? 'success.main' : s.score >= 50 ? 'warning.main' : 'text.secondary',
                    }}
                  >
                    {s.score}%
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}

          <Box display="flex" gap={1} flexWrap="wrap">
            {highConfidence && onAssignTopMatch && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AssignedIcon fontSize="small" />}
                disabled={busy}
                onClick={() => onAssignTopMatch(top.horseId)}
              >
                Assign to {top.horseName}
              </Button>
            )}
            {onAssignManual && (
              <Button
                variant={highConfidence ? 'outlined' : 'contained'}
                size="small"
                disabled={busy}
                onClick={onAssignManual}
              >
                {highConfidence ? 'Different patient...' : 'Assign to patient...'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default UnallocatedItemCard;
